// El calendario del popup "Activá las notificaciones" — ADR-2026-09-16-popup-activar-push.
//
// **Por qué esto vive en `localStorage` y no en la base.** El permiso de notificaciones
// lo da el NAVEGADOR de cada dispositivo: la misma cuenta puede tenerlo activo en el
// teléfono y sin pedir en la computadora. Un contador por usuario diría "ya lo activó"
// en una máquina donde no hay nada activado, y el pedido no volvería a salir nunca.
// `profiles.onboarding_seen` tampoco sirve por una segunda razón: guarda booleanos
// ("ya lo vi"), y acá hace falta recordar CUÁNTAS veces dijo que no y CUÁNDO.
//
// La contra conocida: quien limpia el storage del navegador vuelve a foja cero. Es
// aceptable —también pierde el permiso y la suscripción, así que empezar de nuevo es
// justo lo que corresponde.

/**
 * Cuánto se espera después de cada "Ahora no", en días: la 1ª cancelación espera 3
 * días, la 2ª 7, la 3ª 21 y la 4ª 45.
 *
 * La escalera crece rápido a propósito. Un pedido que vuelve cada tres días para
 * siempre no se lee: se cierra de memoria, y lo que se entrena es el reflejo de
 * cerrar cualquier cosa que aparezca. Cada "no" es información, y la espera que sigue
 * tiene que reconocerla.
 */
export const ESPERAS_DIAS = [3, 7, 21, 45];

/** Al quinto "Ahora no" no se pregunta más. Cinco noes son un no. */
export const MAX_CANCELACIONES = 5;

/** La llave describe al DISPOSITIVO, no a la cuenta: no va en `CLAVES_DE_CUENTA`. */
export const CLAVE_POPUP_PUSH = 'atlas:push-popup';

export interface EstadoPopupPush {
  /** Cuántas veces cerró el popup sin activar. */
  cancelaciones: number;
  /** ISO de la última vez que lo cerró. `null` = no hay registro de cuándo. */
  ultima: string | null;
}

const DIA_MS = 24 * 60 * 60 * 1000;

/**
 * ¿Toca mostrar el popup en este dispositivo?
 *
 * Función pura sobre el estado guardado: no mira el navegador ni el permiso (de eso
 * se ocupa quien la llama, con `usePushNotifications`). Sólo contesta "¿ya esperamos
 * lo suficiente desde el último no?".
 *
 * Ante la duda, MUESTRA. Un estado que no se entiende (storage tocado a mano, una
 * versión vieja del objeto, un ISO inválido) no es evidencia de que la persona haya
 * dicho que no: tratarlo como un "no" perdería el pedido para siempre y en silencio,
 * mientras que mostrarlo de más cuesta un "Ahora no".
 */
export function correspondeMostrar(
  estado: EstadoPopupPush | null,
  ahora: Date,
): boolean {
  if (!estado) return true;

  const canceladas = Number.isFinite(estado.cancelaciones)
    ? Math.max(0, Math.floor(estado.cancelaciones))
    : 0;
  if (canceladas === 0) return true;
  if (canceladas >= MAX_CANCELACIONES) return false;

  // Sin fecha no hay desde cuándo contar. Pasó por acá porque alguien editó el
  // storage o porque una versión futura guardó otra cosa; se muestra.
  if (!estado.ultima) return true;
  const desde = new Date(estado.ultima).getTime();
  if (!Number.isFinite(desde)) return true;

  const espera = ESPERAS_DIAS[canceladas - 1];
  return ahora.getTime() >= desde + espera * DIA_MS;
}

/**
 * Lee el estado del dispositivo. `null` si no hay nada, si el storage está bloqueado
 * (Safari privado) o si lo guardado no tiene la forma esperada — los tres casos
 * significan lo mismo para quien pregunta: "acá no consta ningún no".
 */
export function leerEstadoDelPopup(): EstadoPopupPush | null {
  try {
    const crudo = localStorage.getItem(CLAVE_POPUP_PUSH);
    if (!crudo) return null;
    const dato = JSON.parse(crudo) as Partial<EstadoPopupPush> | null;
    if (!dato || typeof dato !== 'object') return null;
    const cancelaciones = Number(dato.cancelaciones);
    return {
      cancelaciones: Number.isFinite(cancelaciones) ? Math.max(0, Math.floor(cancelaciones)) : 0,
      ultima: typeof dato.ultima === 'string' ? dato.ultima : null,
    };
  } catch {
    return null;
  }
}

/**
 * Anota un "Ahora no": suma uno al contador y sella la fecha. Devuelve el estado que
 * quedó (útil para los tests y para el modo dev).
 *
 * Es best-effort: si el storage está bloqueado no pasa nada y el popup volverá a
 * salir como si fuera la primera vez. En un navegador que no puede recordar nada,
 * ésa es la única conducta posible.
 */
export function registrarCancelacionDelPopup(ahora: Date = new Date()): EstadoPopupPush {
  const previo = leerEstadoDelPopup();
  const estado: EstadoPopupPush = {
    cancelaciones: (previo?.cancelaciones ?? 0) + 1,
    ultima: ahora.toISOString(),
  };
  try {
    localStorage.setItem(CLAVE_POPUP_PUSH, JSON.stringify(estado));
  } catch {
    /* sin storage: el calendario no se puede sostener y el popup arranca de cero */
  }
  return estado;
}

/**
 * Herramientas de consola para probar los escalones sin esperar días. SÓLO en dev:
 * en producción esto no existe y no hay forma de pisar el calendario desde afuera.
 *
 * En la consola del navegador:
 *   atlasPushPopup.ver()            → el estado guardado
 *   atlasPushPopup.reiniciar()      → borra la llave (vuelve a ser la primera vez)
 *   atlasPushPopup.simular(2)       → "canceló 2 veces, la última recién"
 *   atlasPushPopup.simular(2, -8)   → "…hace 8 días" (pasó la espera de 7 → vuelve a salir)
 */
// `typeof window` además de `DEV`: los tests corren en entorno node (sin DOM) y
// vitest también prende `DEV`, así que sin esto el módulo explota al importarse.
if (import.meta.env?.DEV && typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).atlasPushPopup = {
    ver: () => leerEstadoDelPopup(),
    reiniciar: () => {
      try {
        localStorage.removeItem(CLAVE_POPUP_PUSH);
      } catch {
        /* noop */
      }
    },
    simular: (cancelaciones: number, diasAtras = 0) => {
      const ultima = new Date(Date.now() + diasAtras * DIA_MS).toISOString();
      const estado: EstadoPopupPush = { cancelaciones, ultima };
      try {
        localStorage.setItem(CLAVE_POPUP_PUSH, JSON.stringify(estado));
      } catch {
        /* noop */
      }
      return estado;
    },
  };
}
